import React, { useCallback, useMemo } from "react";
import { IonIcon } from "@ionic/react";
import { useHistory } from "react-router-dom";

import { IBoxDashboard, TeacherGearboxDirectory } from "../../../state";
import folderIcon from "../../../assets/icons/folder.svg";
import { manageBoxDirectoriesPath } from "../../../store/teacher";

import styles from "./BoxDirectoryList.module.scss";

type DirectorySelectArgs = {
  directoryName: string;
  directoryId?: number;
  teacherId?: number;
};

type BoxDirectoryListProps = {
  boxList: IBoxDashboard[];
  onDirectorySelect: (args: DirectorySelectArgs) => void;
  teacherId: number;
  isSharedMyClassesBox: (box: IBoxDashboard) => boolean;
  teacherGearboxDirectories: TeacherGearboxDirectory[];
};

const BoxDirectoryList: React.FC<BoxDirectoryListProps> = ({
  boxList,
  onDirectorySelect,
  teacherId,
  isSharedMyClassesBox,
  teacherGearboxDirectories,
}) => {
  const history = useHistory();

  const visibleMyBoxes = useMemo(
    () =>
      boxList.filter(
        box =>
          (box.teacher.id === teacherId || isSharedMyClassesBox(box)) &&
          !box.is_hidden,
      ),
    [boxList, teacherId, isSharedMyClassesBox],
  );

  const getDirectoriesWithBoxCount = useCallback(() => {
    // COREの授業の振り返り用のディレクトリIDを取得
    const coreDirectoryIds = new Set(
      teacherGearboxDirectories.filter(d => d.klass_id).map(d => d.id),
    );

    // COREの授業の振り返り以外のボックスをフィルタリング
    const nonCoreBoxes = visibleMyBoxes.filter(
      box =>
        !box.directory_ids.some(directoryId =>
          coreDirectoryIds.has(directoryId),
        ),
    );

    const directories = teacherGearboxDirectories
      .filter(directory => !directory.klass_id)
      .map(directory => {
        const directoryBoxes = nonCoreBoxes.filter(
          box => box.directory_ids.includes(directory.id) && !box.is_hidden,
        );
        return {
          id: directory.id,
          name: directory.name,
          count: directoryBoxes.length,
        };
      });

    return directories;
  }, [teacherGearboxDirectories, visibleMyBoxes]);

  const getBoxCountByOtherTeacher = useCallback(
    (boxes: IBoxDashboard[]) => {
      // teacherが存在するboxのみをフィルタリング
      const validBoxes = boxes.filter(
        box => box.teacher != null && box.teacher.id !== null,
      );
      // 他の先生のIDを取得(Setは重複を削除するために使う)
      const teacherIds = Array.from(
        new Set(validBoxes.map(box => box.teacher.id)),
      );
      // 自分のIDを取り除いた他の先生のIDを取得
      const otherTeacherIds = teacherIds.filter(id => id !== teacherId);
      // 他の先生のIDごとにBOXの数をカウントする
      return otherTeacherIds.map(id => {
        const teacherBoxes = validBoxes.filter(
          box =>
            box.teacher.id === id &&
            !box.is_hidden &&
            !isSharedMyClassesBox(box),
        );
        const teacher = validBoxes.find(box => box.teacher.id === id)?.teacher;
        return {
          teacherId: id,
          teacherName: teacher?.full_name || "",
          count: teacherBoxes.length,
        };
      });
    },
    [teacherId, isSharedMyClassesBox],
  );

  const totalVisibleBoxCount = visibleMyBoxes.length;

  const otherTeachersDirectories = useMemo(
    () =>
      getBoxCountByOtherTeacher(boxList).map(
        ({ teacherId, teacherName, count }) => ({
          teacherId: teacherId || undefined,
          name: teacherName,
          count,
        }),
      ),
    [boxList, getBoxCountByOtherTeacher],
  );

  const getCoreReflectionDirectories = useCallback(() => {
    // teacherがnullのボックスのみをフィルタリング
    const visibleBoxes = boxList.filter(box => box.teacher.id === null);

    return (teacherGearboxDirectories || [])
      .filter(d => d.klass_id)
      .map(directory => {
        const directoryBoxes = visibleBoxes.filter(box =>
          box.directory_ids.includes(directory.id),
        );
        return {
          id: directory.id,
          name: directory.name,
          count: directoryBoxes.length,
        };
      });
  }, [boxList, teacherGearboxDirectories]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.block}>
        <div className={styles.title}>マイフォルダ</div>
        <ul className={styles.list}>
          <li
            className={styles.item}
            onClick={() => onDirectorySelect({ directoryName: "全て" })}
          >
            <IonIcon className={styles.icon} icon={folderIcon} /> 全て (
            {totalVisibleBoxCount})
          </li>
          {getDirectoriesWithBoxCount().map(directory => (
            <li
              key={directory.id}
              className={styles.item}
              onClick={() =>
                onDirectorySelect({
                  directoryName: directory.name,
                  directoryId: directory.id,
                })
              }
            >
              <IonIcon className={styles.icon} icon={folderIcon} />
              {directory.name} ({directory.count})
            </li>
          ))}
          <li
            className={styles.addFolder}
            onClick={() => history.push(manageBoxDirectoriesPath)}
          >
            <span className={styles.plus}>+</span> フォルダを新規作成
          </li>
        </ul>
      </div>
      <div className={styles.block}>
        <div className={styles.title}>COREの授業の振り返り</div>
        <ul className={styles.list}>
          {getCoreReflectionDirectories().map(directory => (
            <li
              key={directory.id}
              className={styles.item}
              onClick={() =>
                onDirectorySelect({
                  directoryName: directory.name,
                  directoryId: directory.id,
                })
              }
            >
              <IonIcon className={styles.icon} icon={folderIcon} />
              {directory.name} ({directory.count})
            </li>
          ))}
        </ul>
      </div>
      <div className={styles.block}>
        <div className={styles.title}>他の先生のフォルダ</div>
        <ul className={styles.list}>
          {otherTeachersDirectories.map((teacher, index) => (
            <li
              key={index}
              className={styles.item}
              onClick={() =>
                onDirectorySelect({
                  directoryName: teacher.name,
                  teacherId: teacher.teacherId,
                })
              }
            >
              <IonIcon className={styles.icon} icon={folderIcon} />
              {teacher.name} ({teacher.count})
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default BoxDirectoryList;
